import { ActionIcon, Box, Group, Paper, Text, Title } from "@mantine/core";
import { IconGripVertical, IconUsers, IconX } from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { AvatarState } from "../../shared/types";
import { PlayerList } from "./PlayerList";

const PANEL_WIDTH = 260;
const PANEL_MIN_HEIGHT = 120;

function clampPosition(x: number, y: number): { x: number; y: number } {
  const margin = 12;
  const maxX = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin);
  const maxY = Math.max(margin, window.innerHeight - PANEL_MIN_HEIGHT - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

function initialPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 16, y: 84 };
  return clampPosition(16, 84);
}

interface Props {
  players: AvatarState[];
  bots: AvatarState[];
  selfId: string | null;
  friendAddresses?: Set<string>;
  onSelectPlayer?: (avatar: AvatarState) => void;
  onHoverPlayer?: (avatarId: string | null) => void;
  onClose: () => void;
}

export function PlayersPanel({
  players,
  bots,
  selfId,
  friendAddresses,
  onSelectPlayer,
  onHoverPlayer,
  onClose,
}: Props) {
  const [position, setPosition] = useState(initialPosition);
  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const stopDragging = useCallback(() => {
    dragState.current = null;
    document.body.style.userSelect = "";
  }, []);

  const onDragMove = useCallback((event: PointerEvent) => {
    const state = dragState.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    setPosition(clampPosition(state.originX + dx, state.originY + dy));
  }, []);

  const onDragEnd = useCallback(
    function handleDragEnd() {
      stopDragging();
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    },
    [onDragMove, stopDragging]
  );

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragEnd);
      window.removeEventListener("pointercancel", onDragEnd);
      stopDragging();
    };
  }, [onDragEnd, onDragMove, stopDragging]);

  const handleHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
    window.addEventListener("pointercancel", onDragEnd);
  };

  return (
    <Paper
      p={10}
      radius="md"
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        width: PANEL_WIDTH,
        maxHeight: 400,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        pointerEvents: "auto",
      }}
    >
      <Box
        onPointerDown={handleHeaderPointerDown}
        style={{
          touchAction: "none",
          cursor: "grab",
          marginBottom: 6,
          padding: "6px 10px",
          userSelect: "none",
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconGripVertical size={14} color="rgba(0,0,0,0.3)" />
            <IconUsers size={15} color="#ff922b" />
            <Title order={5} fz={13} c="gray.8">
              Players ({players.length + bots.length})
            </Title>
          </Group>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray.4"
            onClick={onClose}
          >
            <IconX size={15} />
          </ActionIcon>
        </Group>
      </Box>

      <Box style={{ flex: 1, overflowY: "auto", padding: "0 10px 4px" }}>
        <PlayerList
          players={players}
          bots={bots}
          selfId={selfId}
          friendAddresses={friendAddresses}
          onSelectPlayer={onSelectPlayer}
          onHoverPlayer={onHoverPlayer}
        />
      </Box>
    </Paper>
  );
}
